import React, { Component, lazy, Suspense } from 'react';
import { Box, Button, Typography, Container, Divider } from '@mui/material';

import { Helmet } from 'react-helmet-async';
import FadeInSection from '../useInView';


const LazyServiceCard = lazy(() => import('./ServiceCard'));
const services = [
    {
        icon: 'appDevelopment',
        title: 'App Development',
        description: 'We build mobile and web apps tailored to your operations, with clean UX, stable performance, and clear user flows that make daily work easier.'
    },
    {
        icon: 'siteDevelopment',
        title: 'Site Development',
        description: 'We create fast, secure websites and e-commerce platforms that load quickly, convert visitors, and stay easy to update as you grow.'
    },
    {
        icon: 'customApplications',
        title: 'Custom Applications',
        description: 'We develop custom software that removes manual steps, integrates with your tools, and improves workflow speed, accuracy, and visibility.'
    },
    {
        icon: 'helpDeskSupport',
        title: 'Help Desk Support',
        description: 'We provide ongoing technical support for your systems and devices, with responsive troubleshooting, clear communication, and preventative maintenance.'
    },
    {
        icon: 'techOnSiteServices',
        title: 'Tech On-Site Services',
        description: 'We handle on-site installations, diagnostics, and repairs for networks, devices, and security systems so your team can keep working without delays.'
    },
    {
        icon: 'automationSolutions',
        title: 'Automation Solutions',
        description: 'We automate tasks and processes to improve efficiency, reduce errors, and free your team to focus on higher-value work.'
    },
    {
        icon: 'artificialIntelligence',
        title: 'Artificial Intelligence (AI)',
        description: 'We use AI tools to automate routines, analyze data, and improve decisions with practical, business-focused workflows you can trust.'
    },

    // — Security, IT & Infrastructure —
    {
        icon: 'cctvInstall',
        title: 'CCTV Installation',
        description: 'We install CCTV systems using Hikvision and UniFi Protect, with clear coverage planning, secure remote access, and reliable storage.'
    },
    {
        icon: 'fireSystems',
        title: 'Fire Alarm Systems',
        description: 'We install and maintain fire alarm systems using compliant hardware, with testing and documentation that keeps your site safe and audit-ready.'
    },
    {
        icon: 'intruderAlarms',
        title: 'Intruder Alarm Systems',
        description: 'We install Texecom, Ajax, and other smart alarm systems with tailored zones, mobile control, and professional setup.'
    },
    {
        icon: 'internetSetup',
        title: 'Internet & Networking',
        description: 'We set up networks using UniFi, Omada, and fibre-ready cabling for stable coverage, secure segmentation, and future-proof growth.'
    },
    {
        icon: 'posSystems',
        title: 'POS Systems',
        description: 'We supply, install, and support POS systems for retail and hospitality with staff training, reporting, and reliable day-to-day performance.'
    }
];


const designs = [
    {
        icon: 'branding',
        title: 'Brand Identity Design',
        description: 'Crafting a clear brand identity that resonates with your audience and scales across web, print, and social without inconsistency.'
    },
    {
        icon: 'branding',
        title: 'Logo Design',
        description: 'Creating memorable logos that communicate your values quickly and work well across signage, digital, and print.'
    },
    {
        icon: 'branding',
        title: 'Graphic Design',
        description: 'From brochures and business cards to banners and packaging, we deliver consistent, on-brand design with clean hierarchy and strong readability.'
    },
    {
        icon: 'branding',
        title: 'Digital Design',
        description: 'Designing digital assets for websites, social media, and email campaigns that are visually consistent and built to convert.'
    },
    {
        icon: 'branding',
        title: 'UI/UX Design',
        description: 'Developing user interfaces and experiences that are intuitive, accessible, and optimized for real user journeys and conversion goals.'
    }
]

const medias = [
    {
        icon: 'video',
        title: 'Video Production',
        description: 'We produce videos for brands, events, and products with structured planning, smooth production, and edits ready for every channel.'
    },
    {
        icon: 'photo',
        title: 'Photography',
        description: 'We provide product, event, and branding photography with clean lighting, strong composition, and consistent editing.'
    },
    {
        icon: 'event',
        title: 'Event Coverage',
        description: 'We cover events with full photo and video support, capturing key moments, speakers, and atmosphere without disruption.'
    },
    {
        icon: 'campaign',
        title: 'Marketing Campaign Visuals',
        description: 'We create campaign visuals for online and print, aligned to your message, audience, and conversion goals.'
    },
    {
        icon: 'production',
        title: 'Post-Production',
        description: 'We handle editing, color grading, and audio cleanup to ensure your content looks polished and professional.'
    },
    {
        icon: 'stream',
        title: 'Live Streaming',
        description: 'We provide live streaming for events, conferences, and launches with stable delivery and clear audio.'
    },

    // — Printology Services (with backlink) —
    {
        icon: 'print',
        title: 'Commercial Printing',
        description: 'We print leaflets, flyers, business cards, menus, booklets, and marketing materials with sharp finishes and consistent color.',
        link: 'https://printology.robotics-media.com'
    },
    {
        icon: 'wideFormat',
        title: 'Wide Format Printing',
        description: 'We produce posters, banners, window graphics, and large-format signage with durable materials and bold visibility.',
        link: 'https://printology.robotics-media.com'
    },
    {
        icon: 'stickers',
        title: 'Stickers & Labels',
        description: 'We print custom stickers, labels, and decals in any shape or size, with finishes that match your brand.',
        link: 'https://printology.robotics-media.com'
    },
    {
        icon: 'design',
        title: 'Graphic Design',
        description: 'We design logos, menus, brochures, posters, and full brand kits with clear hierarchy and print-ready files.',
        link: 'https://printology.robotics-media.com'
    }
];


class Services extends Component {
    render() {
        return (
            <Container style={{ paddingTop: '20px', paddingBottom: '20px', color: 'white', height: '100%' }}>
                <Helmet>
                    <title>Services - Robotics Media</title>
                    <meta name="description" content="Robotics Media services: web development, custom software, IoT and CCTV systems, brand design, photography, video production, and print solutions including 3D printing and laser engraving. Serving businesses across the UK and Romania." />
                </Helmet>
                <Box style={{ height: '150px' }} />
                <FadeInSection>
                    <Typography variant="h2" style={{ textAlign: 'flex-start', marginBottom: '40px', color: 'white' }}>
                        Our Services
                    </Typography>
                </FadeInSection>
                <FadeInSection>
                    <Typography className='code-element' variant='code' style={{ color: 'white', maxWidth: '900px' }}>
                        We help businesses grow with full-stack development, secure IT and CCTV systems, modern branding, content production, and precision print. Explore each service area and start with a free consultation.
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            href="/contact"
                            sx={{
                                bgcolor: 'white',
                                color: 'black',
                                fontFamily: 'PT Mono, monospace',
                                fontWeight: 'bold',
                                borderRadius: '10px',
                                '&:hover': { bgcolor: 'white', opacity: 0.9 },
                            }}
                        >
                            GET A FREE CONSULTATION
                        </Button>
                        <Button
                            variant="outlined"
                            href="tel:+447949628183"
                            sx={{
                                color: 'white',
                                borderColor: 'white',
                                fontFamily: 'PT Mono, monospace',
                                fontWeight: 'bold',
                                borderRadius: '10px',
                                '&:hover': { borderColor: 'white', opacity: 0.9 },
                            }}
                        >
                            BOOK A CALL
                        </Button>
                    </Box>
                </FadeInSection>
                <FadeInSection>
                    <Box sx={{ mt: 5, p: 3, borderRadius: '18px', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(0,0,0,0.35)' }}>
                        <Typography variant="h4" style={{ color: 'white' }}>Who We Work With</Typography>
                        <Box sx={{ height: '12px' }} />
                        <Typography className='code-element' variant='code' style={{ color: 'white', maxWidth: '900px' }}>
                            We work with startups, SMEs, and established businesses in retail, hospitality, corporate, education, and manufacturing. If you need a reliable partner for technology, design, media, or print, we build solutions that fit your stage and budget.
                        </Typography>
                        <Box sx={{ height: '10px' }} />
                        <Typography className='code-element' variant='code' style={{ color: 'white', maxWidth: '900px' }}>
                            Most clients come to us for clearer systems, stronger branding, and better customer experiences. We keep the process simple, transparent, and focused on outcomes.
                        </Typography>
                    </Box>
                </FadeInSection>
                <Box id="target-1" style={{ margin: '40px 0', padding: '10px', borderRadius: '10px' }}>
                    <Box style={{ height: '100px' }} />
                    <FadeInSection>
                        <Typography variant="h3" style={{ color: 'white' }}>IT Wizards</Typography>
                        <Typography className='code-element' variant='code' fontSize='1.5rem' style={{ color: 'white' }}>
                            Unleash the Power of Technology
                        </Typography>
                        <br /><br />
                        <Typography className='code-element' variant='code' style={{ color: 'white' }}>
                            Technology should make your business easier to run, not harder. We design reliable systems that keep teams productive, data secure, and operations smooth. From software builds to on-site support, we focus on solutions that are practical, measurable, and built to last.
                        </Typography>
                        <br />
                        <Typography className='code-element' variant='code' style={{ color: 'white' }}>
                            Expect clear communication, fast response times, and systems that scale as you grow. We take ownership of the full journey, from planning and installation to long-term support.
                        </Typography>
                        <br /> <br />
                        <Typography className='code-element' variant='code' fontSize='1.5rem' style={{ color: 'white' }}>
                            Our Areas of Expertise:
                        </Typography>
                    </FadeInSection>
                    <FadeInSection>
                        <br /><br />
                        {services.map((service, index) => (
                            <Box key={index} mb={2}>
                                {/* Lazy loading the ServiceCard component */}
                                <Suspense fallback={<div>Loading...</div>}>
                                    <LazyServiceCard icon={service.icon} title={service.title} description={service.description} />
                                </Suspense>
                            </Box>
                        ))}
                    </FadeInSection>
                    <br /> <br />
                    <FadeInSection>
                        <Typography className='code-element' variant='code' component='p' style={{ color: 'white' }}>
                            By choosing us, you're not just getting a service provider; you're gaining a partner dedicated to your success in the digital realm. With a comprehensive range of services from development to on-the-ground support, and now including advanced automation and AI solutions, we're here to ensure that your technological infrastructure is robust, reliable, and ready to meet the challenges and opportunities of the digital age.
                        </Typography>
                    </FadeInSection>
                    <FadeInSection>
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                href="/contact"
                                sx={{
                                    bgcolor: 'white',
                                    color: 'black',
                                    fontFamily: 'PT Mono, monospace',
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    '&:hover': { bgcolor: 'white', opacity: 0.9 },
                                }}
                            >
                                START AN IT PROJECT
                            </Button>
                            <Button
                                variant="outlined"
                                href="/contact"
                                sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    fontFamily: 'PT Mono, monospace',
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    '&:hover': { borderColor: 'white', opacity: 0.9 },
                                }}
                            >
                                REQUEST A SITE VISIT
                            </Button>
                        </Box>
                    </FadeInSection>
                    <br />
                    <FadeInSection>
                        <Typography className='code-element' variant='code' component='p' style={{ color: 'white' }}>
                            <a href="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>Let's embark</a> on this journey together and transform your technological challenges into victories.
                        </Typography>
                    </FadeInSection>
                </Box>
                <Divider sx={{ height: '5px', borderRadius: '20px', backgroundColor: 'white' }} />
                <Box id="target-2" style={{ margin: '100px 0', padding: '10px', borderRadius: '10px' }}>
                    <Box style={{ height: '100px' }} />
                    <FadeInSection>
                        <Typography variant="h4" style={{ color: 'white' }}>Design Dreamers</Typography>
                        <Typography className='code-element' variant='code' fontSize='1.5rem' style={{ color: 'white' }}>
                            Bring Your Brand to Life
                        </Typography>
                        <br /><br />
                        <Typography className='code-element' variant='code' style={{ color: 'white' }}>
                            Great design helps customers understand you faster and trust you sooner. We build brand systems that are clean, consistent, and easy to apply across web, print, and social.
                        </Typography>
                        <br />
                        <Typography className='code-element' variant='code' style={{ color: 'white' }}>
                            You will get a clear visual direction, practical assets for everyday use, and a brand that feels confident at every touchpoint.
                        </Typography>
                    </FadeInSection>
                    <FadeInSection>
                        <br /><br />
                        {designs.map((design, index) => (
                            <Box key={index} mb={2}>
                                {/* Lazy loading the ServiceCard component */}
                                <Suspense fallback={<div>Loading...</div>}>
                                    <LazyServiceCard icon={design.icon} title={design.title} description={design.description} />
                                </Suspense>
                            </Box>
                        ))}
                    </FadeInSection>
                    <br /> <br />
                    <FadeInSection>
                        <Typography className='code-element' variant='code' component='p' style={{ color: 'white' }}>
                            By partnering with us, your brand will not only be seen—it will be remembered and cherished. Our Design Dreamers are ready to transform your vision into reality, creating a brand identity that is not only visually stunning but also deeply resonant with your target audience.
                        </Typography>
                    </FadeInSection>
                    <FadeInSection>
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                href="/contact"
                                sx={{
                                    bgcolor: 'white',
                                    color: 'black',
                                    fontFamily: 'PT Mono, monospace',
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    '&:hover': { bgcolor: 'white', opacity: 0.9 },
                                }}
                            >
                                START A BRAND PROJECT
                            </Button>
                            <Button
                                variant="outlined"
                                href="/contact"
                                sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    fontFamily: 'PT Mono, monospace',
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    '&:hover': { borderColor: 'white', opacity: 0.9 },
                                }}
                            >
                                GET A DESIGN QUOTE
                            </Button>
                        </Box>
                        <br />
                        <Typography className='code-element' variant='code' component='p' style={{ color: 'white' }}>
                            Let's start the journey to make your brand unforgettable. <a href="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>Contact us</a> today to explore how our creative solutions can elevate your brand beyond the ordinary.
                        </Typography>
                    </FadeInSection>
                </Box>
                <Divider sx={{ height: '5px', borderRadius: '20px', backgroundColor: 'white' }} />
                <Box id="target-3" style={{ margin: '40px 0', padding: '10px', borderRadius: '10px' }}>
                    <Box style={{ height: '100px' }} />
                    <FadeInSection>
                        <Typography variant="h4" style={{ color: 'white' }}>Media Masters</Typography>
                        <Typography className='code-element' variant='code' fontSize='1.5rem' style={{ color: 'white' }}>
                            Transform Ideas into Media Masterpieces
                        </Typography>
                        <br /><br />
                        <Typography className='code-element' variant='code' style={{ color: 'white' }}>
                            Content should feel natural, on-brand, and built for performance. We plan shoots around your goals, capture the right moments, and deliver edits that work across marketing, sales, and social.
                        </Typography>
                        <br />
                        <Typography className='code-element' variant='code' style={{ color: 'white' }}>
                            The process is simple: clear brief, smooth production, and final assets that are ready to publish.
                        </Typography>
                    </FadeInSection>
                    <FadeInSection>
                        <br /><br />
                        {medias.map((media, index) => (
                            <Box key={index} mb={2}>
                                {/* Lazy loading the ServiceCard component */}
                                <Suspense fallback={<div>Loading...</div>}>
                                    <LazyServiceCard icon={media.icon} title={media.title} description={media.description} />
                                </Suspense>
                            </Box>
                        ))}
                    </FadeInSection>
                    <br /> <br />
                    <FadeInSection>
                        <Typography className='code-element' variant='code' component='p' style={{ color: 'white' }}>
                            By partnering with our Media Masters, you're ensuring that your ideas are not just seen but felt and remembered. Our team is passionate about bringing stories to life, creating content that not only meets but exceeds your expectations.
                        </Typography>
                    </FadeInSection>
                    <FadeInSection>
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                href="/contact"
                                sx={{
                                    bgcolor: 'white',
                                    color: 'black',
                                    fontFamily: 'PT Mono, monospace',
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    '&:hover': { bgcolor: 'white', opacity: 0.9 },
                                }}
                            >
                                PLAN A SHOOT
                            </Button>
                            <Button
                                variant="outlined"
                                href="https://printology.robotics-media.com"
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    fontFamily: 'PT Mono, monospace',
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    '&:hover': { borderColor: 'white', opacity: 0.9 },
                                }}
                            >
                                VISIT PRINTOLOGY
                            </Button>
                        </Box>
                        <br />
                        <Typography className='code-element' variant='code' component='p' style={{ color: 'white' }}>
                            Let us help you transform your ideas into media masterpieces. <a href="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>Contact us</a> today to start your project and make an unforgettable impact with your audience.
                        </Typography>
                    </FadeInSection>
                </Box>
            </Container>
        )
    }
}

export default Services;
